import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import DataTable from "@/components/DataTable";
import { Package, Truck, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = {
  "Received": "bg-slate-100 text-slate-700 border-slate-200",
  "Under Analysis": "bg-amber-50 text-amber-800 border-amber-200",
  "Quotation Pending": "bg-blue-50 text-blue-800 border-blue-200",
  "Quotation Sent": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "PO Received": "bg-teal-50 text-teal-800 border-teal-200",
  "OA Created": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Closed": "bg-slate-100 text-slate-500 border-slate-200",
};

export default function ReceivingList() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/receiving/stats/summary").then((r) => setStats(r.data));
  }, []);

  const fetchData = React.useCallback(async ({ skip, limit, search, filters }) => {
    const params = { skip, limit, search };
    if (filters.status) params.status = filters.status;
    if (filters.customer_id) params.customer_id = filters.customer_id;
    if (filters.analysis_done) params.analysis_done = filters.analysis_done === "true";
    const { data } = await api.get("/receiving", { params });
    return data;
  }, []);

  const columns = [
    { key: "dc_number", label: "DC Number",
      render: (r) => <span className="font-mono text-[13px] font-semibold text-blue-900" data-testid={`dc-${r.id}`}>{r.dc_number}</span> },
    { key: "dc_date", label: "DC Date", className: "font-mono text-xs text-slate-600" },
    { key: "customer_name", label: "Customer", className: "text-slate-800" },
    { key: "plant_name", label: "Plant", className: "text-slate-600 text-xs" },
    { key: "num_cylinders_received", label: "# Cyl",
      render: (r) => <span className="font-mono text-xs font-bold">{r.num_cylinders_received}</span> },
    { key: "transporter", label: "Transporter", className: "text-slate-600 text-xs", visibleByDefault: false },
    { key: "analysis_report_done", label: "Analysis",
      render: (r) => r.analysis_report_done
        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-mono">
            <CheckCircle2 className="w-3 h-3" /> DONE
          </span>
        : <span className="text-[10px] text-slate-400 font-mono">Pending</span> },
    { key: "status", label: "Status",
      render: (r) => {
        const cls = STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700 border-slate-200";
        return <span className={`inline-flex items-center border px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase font-mono ${cls}`}>{r.status}</span>;
      } },
    { key: "created_at", label: "Received On", visibleByDefault: false,
      render: (r) => <span className="font-mono text-[10px] text-slate-500">{r.created_at?.slice(0, 10)}</span> },
  ];

  return (
    <div data-testid="receiving-list-page" className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Reconditioning · Cylinder Received First</div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>Cylinder Receiving</h1>
          <p className="text-sm text-slate-500 mt-1">
            Log incoming cylinders for reconditioning BEFORE any commercial document exists. Cylinder Numbers are assigned only after Order Acceptance is released.
          </p>
        </div>
        <Button data-testid="receiving-new-btn" onClick={() => nav("/receiving/new")}
                className="bg-red-700 hover:bg-red-800 text-white">
          + New Receiving Entry
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            ["Total DCs", stats.total, Package, "text-slate-700", "bg-slate-100"],
            ["Received", stats.by_status["Received"], Truck, "text-blue-800", "bg-blue-50"],
            ["Under Analysis", stats.by_status["Under Analysis"], Package, "text-amber-800", "bg-amber-50"],
            ["Analysis Pending", stats.pending_analysis, Package, "text-orange-700", "bg-orange-50"],
            ["Quotation Sent", stats.by_status["Quotation Sent"], Package, "text-indigo-800", "bg-indigo-50"],
            ["PO Received", stats.by_status["PO Received"], Package, "text-teal-700", "bg-teal-50"],
            ["OA Created", stats.by_status["OA Created"], CheckCircle2, "text-emerald-700", "bg-emerald-50"],
          ].map(([label, value, Icon, cls, bg]) => (
            <Card key={label} data-testid={`rec-widget-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className="border-slate-200 p-3">
              <div className={`w-8 h-8 rounded ${bg} flex items-center justify-center`}>
                <Icon className={`w-4 h-4 ${cls}`} />
              </div>
              <div className={`mt-2 font-mono text-2xl font-bold ${cls}`}>{value ?? 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">{label}</div>
            </Card>
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        fetchData={fetchData}
        filters={[
          { key: "status", label: "Status",
            options: Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s })) },
          { key: "customer_id", label: "Customer", options: customers.map((c) => ({ value: c.id, label: c.name })) },
          { key: "analysis_done", label: "Analysis",
            options: [{ value: "true", label: "Done" }, { value: "false", label: "Pending" }] },
        ]}
        storageKey="receiving-list"
        onRowClick={(r) => nav(`/receiving/${r.id}`)}
        emptyMessage="No cylinders received yet. Click 'New Receiving Entry' to log an incoming DC."
      />
    </div>
  );
}
